// src/components/shared/NotificationBell.js
// Tahap 9b — In-App Notification Bell
// Rollaco Cycling Academy

import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../lib/supabase';

// ── Ikon SVG inline (tidak butuh library) ────────────────────
const BellIcon = ({ hasUnread }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    {hasUnread && (
      <circle cx="18" cy="5" r="4" fill="#ef4444" stroke="#1e293b" strokeWidth="1.5" />
    )}
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ── Icon per tipe notifikasi ─────────────────────────────────
const TYPE_META = {
  session_reminder: { emoji: '🚴', color: '#3b82f6', label: 'Reminder' },
  coach_note:       { emoji: '📝', color: '#8b5cf6', label: 'Catatan Coach' },
  badge_earned:     { emoji: '🏅', color: '#f59e0b', label: 'Badge Baru' },
  schedule_reminder:{ emoji: '📅', color: '#10b981', label: 'Jadwal' },
  athlete_absent:   { emoji: '⚠️', color: '#ef4444', label: 'Absen' },
};

// ── Format waktu relatif ─────────────────────────────────────
function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'baru saja';
  if (m < 60) return `${m} menit lalu`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} jam lalu`;
  const d = Math.floor(h / 24);
  return `${d} hari lalu`;
}

// ── Komponen utama ────────────────────────────────────────────
export default function NotificationBell({ userId }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount]     = useState(0);
  const [open, setOpen]                   = useState(false);
  const [loading, setLoading]             = useState(false);
  const dropdownRef                       = useRef(null);

  // Fetch notifikasi dari Supabase
  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('recipient_id', userId)
        .order('created_at', { ascending: false })
        .limit(30);

      if (error) throw error;
      setNotifications(data || []);
      setUnreadCount((data || []).filter(n => !n.is_read).length);
    } catch (err) {
      console.error('Fetch notifications error:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Realtime subscription — notif baru langsung muncul
  useEffect(() => {
    if (!userId) return;
    fetchNotifications();

    const channel = supabase
      .channel(`notif-${userId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `recipient_id=eq.${userId}`,
      }, (payload) => {
        setNotifications(prev => [payload.new, ...prev]);
        setUnreadCount(prev => prev + 1);
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [userId, fetchNotifications]);

  // Tutup dropdown kalau klik di luar
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Mark satu notif as read
  const markRead = async (id) => {
    await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', id);

    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, is_read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  // Mark semua as read
  const markAllRead = async () => {
    await supabase.rpc('mark_all_notifications_read', { p_recipient_id: userId });
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
  };

  const handleBellClick = () => {
    setOpen(prev => !prev);
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div ref={dropdownRef} style={styles.wrapper}>
      {/* Bell button */}
      <button
        onClick={handleBellClick}
        style={{
          ...styles.bellBtn,
          background: open ? 'rgba(99,102,241,0.18)' : 'rgba(255,255,255,0.06)',
        }}
        title="Notifikasi"
      >
        <BellIcon hasUnread={unreadCount > 0} />
        {unreadCount > 0 && (
          <span style={styles.badge}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={styles.dropdown}>
          {/* Header */}
          <div style={styles.dropHeader}>
            <span style={styles.dropTitle}>Notifikasi</span>
            <div style={styles.dropActions}>
              {unreadCount > 0 && (
                <button onClick={markAllRead} style={styles.markAllBtn}>
                  <CheckIcon /> Tandai semua dibaca
                </button>
              )}
              <button onClick={() => setOpen(false)} style={styles.closeBtn}>
                <CloseIcon />
              </button>
            </div>
          </div>

          {/* List */}
          <div style={styles.list}>
            {loading && (
              <div style={styles.emptyState}>Memuat...</div>
            )}

            {!loading && notifications.length === 0 && (
              <div style={styles.emptyState}>
                <span style={{ fontSize: 32 }}>🔔</span>
                <p style={{ margin: '8px 0 0', color: '#64748b', fontSize: 13 }}>
                  Belum ada notifikasi
                </p>
              </div>
            )}

            {!loading && notifications.map(notif => {
              const meta = TYPE_META[notif.type] || { emoji: '📣', color: '#64748b', label: '' };
              return (
                <div
                  key={notif.id}
                  style={{
                    ...styles.item,
                    background: notif.is_read
                      ? 'transparent'
                      : 'rgba(99,102,241,0.07)',
                    borderLeft: `3px solid ${notif.is_read ? 'transparent' : meta.color}`,
                  }}
                  onClick={() => !notif.is_read && markRead(notif.id)}
                >
                  {/* Emoji icon */}
                  <div style={{ ...styles.itemIcon, background: meta.color + '22' }}>
                    <span style={{ fontSize: 16 }}>{meta.emoji}</span>
                  </div>

                  {/* Content */}
                  <div style={styles.itemContent}>
                    <div style={styles.itemTitle}>
                      {notif.title}
                      {!notif.is_read && <span style={styles.dot} />}
                    </div>
                    <div style={styles.itemMsg}>{notif.message}</div>
                    <div style={styles.itemTime}>{timeAgo(notif.created_at)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────
const styles = {
  wrapper: {
    position: 'relative',
    display: 'inline-block',
  },
  bellBtn: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    border: 'none',
    borderRadius: 10,
    cursor: 'pointer',
    color: '#cbd5e1',
    transition: 'background 0.2s',
  },
  badge: {
    position: 'absolute',
    top: 4,
    right: 4,
    minWidth: 16,
    height: 16,
    padding: '0 4px',
    borderRadius: 8,
    background: '#ef4444',
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    border: '1.5px solid #0f172a',
  },
  dropdown: {
    position: 'fixed',
    top: 70,
    left: 8,
    right: 8,
    width: 'auto',
    maxHeight: 480,
    background: '#1e293b',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 14,
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    zIndex: 9999,
    animation: 'notifSlideIn 0.18s ease',
  },
  dropHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.07)',
    flexShrink: 0,
  },
  dropTitle: {
    fontWeight: 700,
    fontSize: 15,
    color: '#f1f5f9',
    letterSpacing: '-0.01em',
  },
  dropActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  markAllBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    background: 'rgba(99,102,241,0.15)',
    border: '1px solid rgba(99,102,241,0.3)',
    borderRadius: 6,
    color: '#a5b4fc',
    fontSize: 11,
    fontWeight: 600,
    padding: '4px 8px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  closeBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 26,
    height: 26,
    background: 'rgba(255,255,255,0.05)',
    border: 'none',
    borderRadius: 6,
    color: '#64748b',
    cursor: 'pointer',
  },
  list: {
    overflowY: 'auto',
    flex: 1,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    color: '#64748b',
    fontSize: 13,
  },
  item: {
    display: 'flex',
    gap: 12,
    padding: '12px 16px',
    cursor: 'pointer',
    transition: 'background 0.15s',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
  },
  itemIcon: {
    width: 38,
    height: 38,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  itemContent: {
    flex: 1,
    minWidth: 0,
  },
  itemTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    fontWeight: 600,
    color: '#f1f5f9',
    marginBottom: 3,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#6366f1',
    flexShrink: 0,
  },
  itemMsg: {
    fontSize: 12,
    color: '#94a3b8',
    lineHeight: 1.5,
    marginBottom: 4,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  itemTime: {
    fontSize: 11,
    color: '#475569',
  },
};

// ── CSS animasi (inject sekali ke document head) ──────────────
if (typeof document !== 'undefined') {
  const styleId = 'notif-bell-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes notifSlideIn {
        from { opacity: 0; transform: translateY(-6px) scale(0.97); }
        to   { opacity: 1; transform: translateY(0)   scale(1);    }
      }
    `;
    document.head.appendChild(style);
  }
}